import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingCart, Users, Check, AlertCircle, Loader } from 'lucide-react';
import { createNinjaSubscription } from '../services/api';

export default function NinjaPurchaseModal({ isOpen, onClose, currentLimits }) {
  const [quantity, setQuantity] = useState(1);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [showPayPal, setShowPayPal] = useState(false);
  const [subscriptionId, setSubscriptionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Precios
  const NINJA_PRICE_MONTHLY = 79; // MXN
  const NINJA_PRICE_ANNUAL = 850; // MXN

  // Calcular precio total
  const pricePerProfile = billingCycle === 'annual' ? NINJA_PRICE_ANNUAL : NINJA_PRICE_MONTHLY;
  const totalPrice = pricePerProfile * quantity;
  const newLimit = (currentLimits?.total || 0) + quantity;

  // Calcular ahorro anual
  const annualSavings = useMemo(() => {
    if (billingCycle === 'annual') {
      const monthlyTotal = NINJA_PRICE_MONTHLY * 12 * quantity;
      const annualTotal = NINJA_PRICE_ANNUAL * quantity;
      return monthlyTotal - annualTotal;
    }
    return 0;
  }, [billingCycle, quantity]);

  const handleQuantityChange = (delta) => {
    const newQty = quantity + delta;
    if (newQty >= 1 && newQty <= 10) {
      setQuantity(newQty);
    }
  };

  const handleContinue = async () => {
    try {
      setError(null);
      setLoading(true);
      
      console.log('🥷 Creando suscripción de ninjas...', { quantity, billingCycle });
      
      // Crear suscripción en el backend
      const response = await createNinjaSubscription({ quantity, billingCycle });
      
      if (response.success && response.approvalUrl) {
        console.log('✅ Suscripción creada, redirigiendo a PayPal...');
        // Redirigir al usuario a PayPal para aprobar la suscripción
        window.location.href = response.approvalUrl;
      } else {
        throw new Error(response.message || 'Error al crear la suscripción');
      }
    } catch (err) {
      console.error('❌ Error creando suscripción:', err);
      setError(err.message || 'Error al crear la suscripción. Por favor, intenta de nuevo.');
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (success) {
      // Si hubo éxito, recargar la página para actualizar los límites
      window.location.reload();
    } else {
      setQuantity(1);
      setBillingCycle('monthly');
      setShowPayPal(false);
      setSubscriptionId(null);
      setError(null);
      setSuccess(false);
      onClose();
    }
  };

  const handleSubscriptionSuccess = (paypalSubscriptionId) => {
    console.log('✅ Suscripción de ninjas aprobada:', paypalSubscriptionId);
    setSubscriptionId(paypalSubscriptionId);
    setSuccess(true);
    setError(null);
    
    // Mostrar mensaje y esperar antes de recargar
    setTimeout(() => {
      handleClose();
    }, 2000);
  };

  const handleSubscriptionError = (errorMessage) => {
    console.error('❌ Error en suscripción de ninjas:', errorMessage);
    setError(errorMessage || 'Error al procesar la suscripción. Por favor, intenta de nuevo.');
    setLoading(false);
  };

  const handleSubscriptionCancel = () => {
    console.log('ℹ️ Usuario canceló la suscripción de ninjas');
    setShowPayPal(false);
    setError('Suscripción cancelada. Puedes intentar de nuevo.');
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        onClick={(e) => e.target === e.currentTarget && !loading && handleClose()}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl"
        >
          {/* Header */}
          <div className="sticky top-0 z-10 bg-morado px-6 py-4 text-white rounded-t-2xl">
            <button
              onClick={handleClose}
              disabled={loading}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/20 transition-colors disabled:opacity-50"
            >
              <X className="w-6 h-6" />
            </button>
            
            <div className="flex items-center gap-3">
              <ShoppingCart className="w-8 h-8" />
              <div>
                <h2 className="text-2xl font-bold">Comprar Perfiles Ninja Adicionales</h2>
                <p className="text-sm opacity-90 mt-1">
                  Agrega más perfiles para familiares o clientes
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            
            {/* Error Message */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg"
              >
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </motion.div>
            )}

            {!showPayPal && (
              <>
                {/* Current Limits */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Límite Actual</h3>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-morado">
                      {currentLimits?.total || 0}
                    </span>
                    <span className="text-sm text-gray-600">perfil(es) Ninja disponibles</span>
                  </div>
                  <div className="mt-2 text-xs text-gray-500">
                    Base: {currentLimits?.base || 0} | 
                    Agregados: {currentLimits?.manuallyAdded || 0} | 
                    Comprados: {currentLimits?.purchased || 0}
                  </div>
                </div>

                {/* Quantity Selector */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    ¿Cuántos perfiles adicionales necesitas?
                  </label>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => handleQuantityChange(-1)}
                      disabled={quantity <= 1}
                      className="w-12 h-12 rounded-full border-2 border-morado text-morado font-bold text-xl hover:bg-morado hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      −
                    </button>
                    
                    <div className="flex-1 text-center">
                      <div className="text-4xl font-bold text-morado">{quantity}</div>
                      <div className="text-sm text-gray-600 mt-1">
                        {quantity === 1 ? 'perfil' : 'perfiles'}
                      </div>
                    </div>
                    
                    <button
                      onClick={() => handleQuantityChange(1)}
                      disabled={quantity >= 10}
                      className="w-12 h-12 rounded-full border-2 border-morado text-morado font-bold text-xl hover:bg-morado hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      +
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 text-center mt-2">
                    Máximo: 10 perfiles por compra
                  </p>
                </div>

                {/* Billing Cycle */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Ciclo de Facturación
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setBillingCycle('monthly')}
                      className={`relative p-4 rounded-lg border-2 transition-all ${
                        billingCycle === 'monthly'
                          ? 'border-morado bg-morado/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {billingCycle === 'monthly' && (
                        <div className="absolute top-2 right-2">
                          <Check className="w-5 h-5 text-morado" />
                        </div>
                      )}
                      <div className="text-left">
                        <div className="font-semibold text-gray-900">Mensual</div>
                        <div className="text-2xl font-bold text-morado mt-1">
                          ${NINJA_PRICE_MONTHLY}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">por perfil/mes</div>
                      </div>
                    </button>

                    <button
                      onClick={() => setBillingCycle('annual')}
                      className={`relative p-4 rounded-lg border-2 transition-all ${
                        billingCycle === 'annual'
                          ? 'border-morado bg-morado/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {billingCycle === 'annual' && (
                        <div className="absolute top-2 right-2">
                          <Check className="w-5 h-5 text-morado" />
                        </div>
                      )}
                      <div className="text-left">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900">Anual</span>
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">
                            -10%
                          </span>
                        </div>
                        <div className="text-2xl font-bold text-morado mt-1">
                          ${NINJA_PRICE_ANNUAL}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">por perfil/año</div>
                      </div>
                    </button>
                  </div>
                  {billingCycle === 'annual' && annualSavings > 0 && (
                    <p className="text-sm text-green-600 font-semibold mt-2 text-center">
                      ¡Ahorras ${annualSavings} MXN al año!
                    </p>
                  )}
                </div>

                {/* Summary */}
                <div className="bg-morado/10 rounded-lg p-5 border-2 border-morado/20">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Resumen de Compra</h3>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Cantidad:</span>
                      <span className="font-semibold text-gray-900">{quantity} perfil(es)</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Precio por perfil:</span>
                      <span className="font-semibold text-gray-900">
                        ${pricePerProfile} MXN/{billingCycle === 'annual' ? 'año' : 'mes'}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Límite actual:</span>
                      <span className="font-semibold text-gray-900">{currentLimits?.total || 0}</span>
                    </div>
                    <div className="flex justify-between text-sm font-bold text-green-600">
                      <span>Nuevo límite:</span>
                      <span>{newLimit} perfil(es)</span>
                    </div>
                  </div>

                  <div className="pt-3 border-t border-morado/20">
                    <div className="flex justify-between items-baseline">
                      <span className="text-gray-700 font-semibold">Total:</span>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-morado">
                          ${totalPrice} MXN
                        </div>
                        <div className="text-xs text-gray-600">
                          {billingCycle === 'annual' ? 'Pago anual' : 'Pago mensual'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Aviso de Renovación Automática */}
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <div className="flex gap-3">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-green-900 mb-1">
                        🔄 Renovación Automática
                      </p>
                      <p className="text-xs text-green-700 leading-relaxed">
                        Esta suscripción se renovará automáticamente cada {billingCycle === 'annual' ? 'año' : 'mes'}. 
                        Puedes cancelarla en cualquier momento desde tu cuenta de PayPal.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Continue Button */}
                <button
                  onClick={handleContinue}
                  disabled={loading}
                  className="w-full bg-morado text-white font-semibold py-4 rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      Procesando...
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="w-5 h-5" />
                      Continuar al Pago
                    </>
                  )}
                </button>
              </>
            )}

            {/* Info */}
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <div className="flex gap-3">
                <Users className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-blue-900 mb-1">
                    ¿Qué es un Perfil Adicional (Ninja)?
                  </p>
                  <p className="text-xs text-blue-700 leading-relaxed">
                    Es una función que te permite agregar y consultar la información numerológica 
                    de otra persona (como tu pareja, hijos, familiares o clientes) y puedas también 
                    ver la información de sus días y energías numerológicas.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

